import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/PageTitle/PageTitle";
import Table from "../../components/Table/Table";
import { TableSearch } from "../../components/Table/TableActions";
import Badge from "../../components/Badge/Badge";
import Tabs from "@mui/lab/TabList";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import { Tab } from "@mui/material";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { httprequest } from "../../data/api";
import { toast, ToastContainer } from "react-toastify";
import PageLoader from "../../components/PageLoader/pageloader";

const SUBMISSION_FLAGS_ENDPOINT = "/admin/v1/submission-flags";
const LESSON_NOTE_REVIEWS_ENDPOINT = "/admin/v1/lesson-note-reviews";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }

  return d.toLocaleDateString(navigator.language, { day: "2-digit", month: "short", year: "numeric" });
};

const Rewards = () => {
  const [tabValue, setTabValue] = useState("1");
  const [flaggedData, setFlaggedData] = useState([]);
  const [reviewedData, setReviewedData] = useState([]);
  const [flaggedSearch, setFlaggedSearch] = useState([]);
  const [reviewedSearch, setReviewedSearch] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const fetchSubmissionFlags = () => {
    return httprequest
      .get(SUBMISSION_FLAGS_ENDPOINT)
      .then((res) => {
        const payload = res?.data?.data;
        const list = Array.isArray(payload) ? payload : [];

        const mapped = list.map((item) => {
          const teacher = item.teacher || {};
          const submission = item.submission || {};
          const review = item.review || {};

          const teacherName = `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim() || teacher.email || "-";
          const klass = submission.class_name || submission.class || "-";

          let task = "-";
          if (submission.type === "lesson_note") {
            task = "Lesson Note";
          } else if (submission.type) {
            task = submission.type;
          }

          return {
            id: item.id,
            teacher: teacherName,
            class: klass,
            task,
            points: review.awarded_points ?? review.points ?? "",
            date: formatDate(item.created_at),
            status: review.status || item.flag_type || "-"
          };
        });

        setFlaggedData(mapped);
        setFlaggedSearch(mapped);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load submission flags");
      });
  };

  const fetchLessonNoteReviews = () => {
    return httprequest
      .get(LESSON_NOTE_REVIEWS_ENDPOINT)
      .then((res) => {
        const payload = res?.data?.data;
        const list = Array.isArray(payload) ? payload : [];

        const mapped = list.map((item) => {
          const teacher = item.teacher || {};
          const submission = item.submission || {};
          const review = item.review || {};

          const teacherName = `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim() || teacher.email || "-";
          const klass = submission.class_name || submission.class || "-";

          let task = "-";
          if (submission.type === "lesson_note") {
            task = "Lesson Note";
          } else if (submission.type) {
            task = submission.type;
          }

          return {
            id: item.id,
            teacher: teacherName,
            class: klass,
            task,
            points: review.adjusted_points ?? review.original_points ?? "",
            date: formatDate(item.created_at),
            status: item.status || review.status || "-"
          };
        });

        setReviewedData(mapped);
        setReviewedSearch(mapped);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load reviewed submissions");
      });
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchSubmissionFlags(), fetchLessonNoteReviews()])
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
  };

  const handleFlaggedFilter = (e) => {
    const { value } = e.target;
    const result = flaggedData.filter((row) => {
      return row.teacher?.toLowerCase().includes(value.toLowerCase()) ||
      row.task?.toLowerCase().includes(value.toLowerCase()) ||
      row.class?.toLowerCase().includes(value.toLowerCase());
    });
    setFlaggedSearch(result);
  };

  const handleReviewedFilter = (e) => {
    const { value } = e.target;
    const result = reviewedData.filter((row) => {
      return row.teacher?.toLowerCase().includes(value.toLowerCase()) ||
      row.task?.toLowerCase().includes(value.toLowerCase()) ||
      row.class?.toLowerCase().includes(value.toLowerCase());
    });
    setReviewedSearch(result);
  };

  const columns = [
    {
      name: "Teacher",
      selector: (row) => row?.teacher,
      sortable: true
    },
    {
      name: "Task",
      selector: (row) => row?.task || "Lesson Note",
      sortable: true
    },
    {
      name: "Date",
      selector: (row) => row?.date,
      sortable: true
    },
    {
      name: "Status",
      sortable: true,
      cell: (row) => <Badge status={row?.status?.toLowerCase()} />
    },
    {
      name: "",
      center: true,
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button className="table-action-icon" onClick={() => navigate(`/rewards/${row?.id}`)}>
            <Icon icon="fluent:eye-16-regular" />
          </button>
          <button className="table-action-icon delete-icon">
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <PageTitle title="Rewards" subtitle="Manage flagged and reviewed teacher submissions." />
      <TabContext value={tabValue}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="rewards tabs"
          TabIndicatorProps={{ style: { background: "#1a66f5" } }}
        >
          <Tab label="Flagged Submissions" value="1" />
          <Tab label="Reviewed Submissions" value="2" />
        </Tabs>
        <TabPanel value="1" className="tabpanel-overide">
          {isLoading ? (
            <PageLoader />
          ) : (
            <Table
              data={flaggedSearch}
              columns={columns}
              subHeader={true}
              pagination
              subHeaderComponent={
                <TableSearch placeholder="Search here..." searchTableFunc={handleFlaggedFilter} />
              }
            />
          )}
        </TabPanel>
        <TabPanel value="2" className="tabpanel-overide">
          {isLoading ? (
            <PageLoader />
          ) : (
            <Table
              data={reviewedSearch}
              columns={columns}
              subHeader={true}
              pagination
              subHeaderComponent={
                <TableSearch placeholder="Search here..." searchTableFunc={handleReviewedFilter} />
              }
            />
          )}
        </TabPanel>
      </TabContext>
      <ToastContainer />
    </Layout>
  );
};

export default Rewards;
